import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Calculator,
  CalendarDays,
  Check,
  CheckSquare,
  ChevronRight,
  CircleDot,
  Filter,
  Plus,
  Settings,
  Trash2,
  Wallet,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Badge } from "../components/Badge";
import { ErrorPanel, Loading } from "../components/Loading";
import { BilingualText } from "../components/ui/BilingualText";
import { BilingualTextWithTranslate } from "../components/ui/BilingualTextWithTranslate";
import {
  EntityDrawer,
  PageHeader,
  StatutoryDefaultsCallout,
} from "../components/ui";
import { api, ApiError } from "../lib/api";
import type { LeaveTypeDetail } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

// Master Leave Type catalog page (M15.2). Two-pane layout:
//   left   — filterable list
//   right  — selected leave-type detail with 6 tabbed sections
//
// Each editable field saves via PATCH on blur/Enter. Inline edit on
// every field keeps the page compact (a full per-section form would
// stretch to multiple screens given the 60-field set).

const CATEGORIES = [
  "annual",
  "sick",
  "emergency",
  "unpaid",
  "hajj",
  "maternity",
  "paternity",
  "bereavement",
  "marriage",
  "iddah",
  "exam",
  "hardship_travel",
  "other",
] as const;

const BALANCE_CALC_TYPES = [
  "fixed_annual",
  "monthly_accrual",
  "service_tiered",
  "once_per_lifetime",
  "as_needed",
  "unlimited",
] as const;

const SECTIONS = [
  { key: "identification", labelKey: "policies.leave_types_tab_identification", icon: CircleDot },
  { key: "eligibility", labelKey: "policies.leave_types_tab_eligibility", icon: Filter },
  { key: "balance", labelKey: "policies.leave_types_tab_balance", icon: CalendarDays },
  { key: "payment", labelKey: "policies.leave_types_tab_payment", icon: Wallet },
  { key: "calculation", labelKey: "policies.leave_types_tab_calculation", icon: Calculator },
  { key: "validation", labelKey: "policies.leave_types_tab_validation", icon: CheckSquare },
  { key: "pr1_knobs", labelKey: "policies.leave_types_tab_pr1_knobs", icon: Settings },
] as const;
type Section = (typeof SECTIONS)[number]["key"];

function categoryTone(cat: string): "green" | "amber" | "rose" | "sky" | "slate" {
  if (["annual"].includes(cat)) return "green";
  if (["sick", "hardship_travel", "emergency"].includes(cat)) return "amber";
  if (["unpaid"].includes(cat)) return "slate";
  if (["maternity", "paternity", "marriage", "bereavement", "iddah"].includes(cat))
    return "rose";
  return "sky";
}

// -----------------------------------------------------------------
// Inline-edit field — string + number variants
// -----------------------------------------------------------------

function Field({
  label,
  value,
  onSave,
  type = "text",
  options,
}: {
  label: string;
  value: string | number | boolean | null;
  onSave: (v: string | number | boolean | null) => void;
  type?: "text" | "number" | "checkbox" | "select";
  options?: readonly string[];
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value === null ? "" : String(value));

  if (type === "checkbox") {
    return (
      <label className="flex items-center justify-between gap-2 rounded border border-slate-200 px-3 py-1.5 text-sm">
        <span className="text-slate-700">{label}</span>
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onSave(e.target.checked)}
          className="h-4 w-4"
        />
      </label>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </span>
      {editing ? (
        type === "select" && options ? (
          <select
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              if (draft !== (value === null ? "" : String(value))) {
                onSave(draft || null);
              }
              setEditing(false);
            }}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">—</option>
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        ) : (
          <input
            autoFocus
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const parsed =
                  type === "number" && draft !== ""
                    ? Number(draft)
                    : draft || null;
                onSave(parsed);
                setEditing(false);
              }
              if (e.key === "Escape") setEditing(false);
            }}
            onBlur={() => {
              if (draft !== (value === null ? "" : String(value))) {
                const parsed =
                  type === "number" && draft !== ""
                    ? Number(draft)
                    : draft || null;
                onSave(parsed);
              }
              setEditing(false);
            }}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          />
        )
      ) : (
        <button
          type="button"
          onClick={() => {
            setDraft(value === null ? "" : String(value));
            setEditing(true);
          }}
          className="text-start text-sm hover:text-slate-700"
        >
          {value === null || value === "" ? (
            <span className="italic text-slate-400">—</span>
          ) : (
            <span className="font-mono tabular-nums">{String(value)}</span>
          )}
        </button>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// Per-section pane
// -----------------------------------------------------------------

function IdentificationSection({
  lt,
  patch,
}: {
  lt: LeaveTypeDetail;
  patch: (body: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-4">
      <Field label={t("policies.lt_field_code")} value={lt.code} onSave={() => {}} />
      <Field
        label={t("policies.lt_field_name_en")}
        value={lt.name_en}
        onSave={(v) => patch({ name_en: v })}
      />
      <Field
        label={t("policies.lt_field_name_ar")}
        value={lt.name_ar}
        onSave={(v) => patch({ name_ar: v })}
      />
      <Field
        label={t("policies.lt_field_mask_en")}
        value={lt.mask_en}
        onSave={(v) => patch({ mask_en: v })}
      />
      <Field
        label={t("policies.lt_field_mask_ar")}
        value={lt.mask_ar}
        onSave={(v) => patch({ mask_ar: v })}
      />
      <Field
        label={t("policies.lt_field_color")}
        value={lt.leave_color}
        onSave={(v) => patch({ leave_color: v })}
      />
      <div className="col-span-2">
        <Field
          label={t("policies.lt_field_description")}
          value={lt.description}
          onSave={(v) => patch({ description: v })}
        />
      </div>
    </div>
  );
}

function EligibilitySection({
  lt,
  patch,
}: {
  lt: LeaveTypeDetail;
  patch: (body: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-4">
      <Field
        label={t("policies.lt_field_category")}
        value={lt.category}
        onSave={(v) => patch({ category: v })}
        type="select"
        options={CATEGORIES}
      />
      <Field
        label={t("policies.lt_field_ticket_category")}
        value={lt.ticket_category}
        onSave={(v) => patch({ ticket_category: v })}
        type="select"
        options={["none", "annual_ticket", "hajj_ticket", "emergency_ticket"]}
      />
      <Field
        label={t("policies.lt_field_religion_filter")}
        value={lt.religion_filter}
        onSave={(v) => patch({ religion_filter: v })}
      />
      <Field
        label={t("policies.lt_field_gender_filter")}
        value={lt.gender_filter}
        onSave={(v) => patch({ gender_filter: v })}
        type="select"
        options={["male", "female"]}
      />
      <Field
        label={t("policies.lt_field_marital_filter")}
        value={lt.marital_status_filter}
        onSave={(v) => patch({ marital_status_filter: v })}
        type="select"
        options={["single", "married", "divorced", "widowed"]}
      />
      <Field
        label={t("policies.lt_field_time_basis")}
        value={lt.eligibility_time_basis}
        onSave={(v) => patch({ eligibility_time_basis: v })}
        type="select"
        options={[
          "service_period",
          "join_date",
          "calendar_year",
          "fiscal_year",
        ]}
      />
      <Field
        label={t("policies.lt_field_eligible_after")}
        value={lt.eligible_after_months}
        onSave={(v) => patch({ eligible_after_months: v })}
        type="number"
      />
      <Field
        label={t("policies.lt_field_ticket_eligible_after")}
        value={lt.ticket_eligible_after_months}
        onSave={(v) => patch({ ticket_eligible_after_months: v })}
        type="number"
      />
      <Field
        label={t("policies.lt_field_nationality_filter")}
        value={
          lt.nationality_filter ? JSON.stringify(lt.nationality_filter) : null
        }
        onSave={(v) => {
          if (!v) return patch({ nationality_filter: null });
          try {
            patch({ nationality_filter: JSON.parse(String(v)) });
          } catch {
            /* ignore */
          }
        }}
      />
    </div>
  );
}

function BalanceSection({
  lt,
  patch,
}: {
  lt: LeaveTypeDetail;
  patch: (body: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4">
        <Field
          label={t("policies.lt_field_calc_type")}
          value={lt.balance_calculation_type}
          onSave={(v) => patch({ balance_calculation_type: v })}
          type="select"
          options={BALANCE_CALC_TYPES}
        />
        <Field
          label={t("policies.lt_field_num_days")}
          value={lt.number_of_days}
          onSave={(v) => patch({ number_of_days: v })}
          type="number"
        />
        <Field
          label={t("policies.lt_field_days_per_tx")}
          value={lt.days_per_transaction}
          onSave={(v) => patch({ days_per_transaction: v })}
          type="number"
        />
        <Field
          label={t("policies.lt_field_every_no_days")}
          value={lt.every_no_of_days}
          onSave={(v) => patch({ every_no_of_days: v })}
          type="number"
        />
        <Field
          label={t("policies.lt_field_max_negative")}
          value={lt.maximum_negative_days}
          onSave={(v) => patch({ maximum_negative_days: v })}
          type="number"
        />
        <Field
          label={t("policies.lt_field_carry_over_days")}
          value={lt.carry_over_days}
          onSave={(v) => patch({ carry_over_days: v })}
          type="number"
        />
        <Field
          label={t("policies.lt_field_weekend_eligibility")}
          value={lt.weekend_eligibility}
          onSave={(v) => patch({ weekend_eligibility: v })}
          type="select"
          options={["included", "excluded", "partial"]}
        />
        <Field
          label={t("policies.lt_field_weekend_num_days")}
          value={lt.weekend_number_of_days}
          onSave={(v) => patch({ weekend_number_of_days: v })}
          type="number"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field
          label={t("policies.lt_field_remove_holidays")}
          value={lt.remove_holidays_from_transaction}
          onSave={(v) => patch({ remove_holidays_from_transaction: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_remove_weekends")}
          value={lt.remove_weekends_from_transaction}
          onSave={(v) => patch({ remove_weekends_from_transaction: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_allow_negative")}
          value={lt.allow_negative_balance}
          onSave={(v) => patch({ allow_negative_balance: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_balanced")}
          value={lt.balanced}
          onSave={(v) => patch({ balanced: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_ticket_balanced")}
          value={lt.ticket_balanced}
          onSave={(v) => patch({ ticket_balanced: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_convert_to_absence")}
          value={lt.convert_to_absence_when_no_balance}
          onSave={(v) => patch({ convert_to_absence_when_no_balance: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_carry_over_service")}
          value={lt.leave_carry_over_by_service_period}
          onSave={(v) => patch({ leave_carry_over_by_service_period: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_encashable_eos")}
          value={lt.encashable_on_eos}
          onSave={(v) => patch({ encashable_on_eos: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_deduct_service")}
          value={lt.days_will_be_deducted_from_service_period}
          onSave={(v) => patch({ days_will_be_deducted_from_service_period: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_activate_days_prior")}
          value={lt.activate_days_prior_leave}
          onSave={(v) => patch({ activate_days_prior_leave: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_wfh_active")}
          value={lt.active_days_work_from_home}
          onSave={(v) => patch({ active_days_work_from_home: v })}
          type="checkbox"
        />
      </div>
    </div>
  );
}

function PaymentSection({
  lt,
  patch,
}: {
  lt: LeaveTypeDetail;
  patch: (body: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4">
        <Field
          label={t("policies.lt_field_pay_ranges_per")}
          value={lt.payment_ranges_per}
          onSave={(v) => patch({ payment_ranges_per: v })}
          type="select"
          options={["leave_day", "calendar_day", "working_day"]}
        />
        <Field
          label={t("policies.lt_field_min_days_advance")}
          value={lt.min_days_to_get_salary_in_advance}
          onSave={(v) => patch({ min_days_to_get_salary_in_advance: v })}
          type="number"
        />
        <Field
          label={t("policies.lt_field_auto_return_before")}
          value={lt.auto_return_before_days}
          onSave={(v) => patch({ auto_return_before_days: v })}
          type="number"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field
          label={t("policies.lt_field_requires_attachment")}
          value={lt.requires_attachment_before_request}
          onSave={(v) => patch({ requires_attachment_before_request: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_pay_with_salary")}
          value={lt.pay_leave_with_salary}
          onSave={(v) => patch({ pay_leave_with_salary: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_include_in_pay_cycle")}
          value={lt.include_in_pay_cycle}
          onSave={(v) => patch({ include_in_pay_cycle: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_auto_return")}
          value={lt.auto_return}
          onSave={(v) => patch({ auto_return: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_allow_expired_contract")}
          value={lt.allow_users_with_expired_contract_to_request}
          onSave={(v) =>
            patch({ allow_users_with_expired_contract_to_request: v })
          }
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_hide_balance")}
          value={lt.hide_leave_balance}
          onSave={(v) => patch({ hide_leave_balance: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_activate_pay_advance")}
          value={lt.activate_pay_in_advance}
          onSave={(v) => patch({ activate_pay_in_advance: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_vacation_alt_mandatory")}
          value={lt.vacation_alternative_mandatory}
          onSave={(v) => patch({ vacation_alternative_mandatory: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_carry_over_after_cutoff")}
          value={lt.carry_over_after_cutoff_date}
          onSave={(v) => patch({ carry_over_after_cutoff_date: v })}
          type="checkbox"
        />
        <Field
          label={t("policies.lt_field_fill_to_date")}
          value={lt.fill_to_date_based_on_balance}
          onSave={(v) => patch({ fill_to_date_based_on_balance: v })}
          type="checkbox"
        />
      </div>
    </div>
  );
}

function CalculationSection({
  lt,
  patch,
}: {
  lt: LeaveTypeDetail;
  patch: (body: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <Field
        label={t("policies.lt_field_rule_id_amount")}
        value={lt.rule_id_for_amount_calculation}
        onSave={(v) => patch({ rule_id_for_amount_calculation: v })}
      />
      <p className="text-xs italic text-slate-500">
        {t("policies.lt_calc_help")}
      </p>
    </div>
  );
}

function Pr1KnobsSection({
  lt,
  patch,
}: {
  lt: LeaveTypeDetail;
  patch: (body: Record<string, unknown>) => void;
}) {
  // §1.X — 14 M3X.LEAVES PR1 knobs that previously had no editor
  // surface. Backend ORM columns exist; this section makes them
  // editable.
  const { t } = useTranslation();
  const ltAny = lt as unknown as Record<string, unknown>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field
        label={t("policies.lt_field_activate_pay_in_advance", "Activate pay-in-advance")}
        value={ltAny.activate_pay_in_advance as boolean | null}
        onSave={(v) => patch({ activate_pay_in_advance: v })}
        type="checkbox"
      />
      <Field
        label={t("policies.lt_field_min_days_to_get_salary_in_advance", "Min days to get salary in advance")}
        value={ltAny.min_days_to_get_salary_in_advance as number | null}
        onSave={(v) => patch({ min_days_to_get_salary_in_advance: v })}
        type="number"
      />
      <Field
        label={t("policies.lt_field_pay_in_advance_activate", "Pay-in-advance activate")}
        value={ltAny.pay_in_advance_activate as boolean | null}
        onSave={(v) => patch({ pay_in_advance_activate: v })}
        type="checkbox"
      />
      <Field
        label={t("policies.lt_field_pay_in_advance_min_days", "Pay-in-advance min days")}
        value={ltAny.pay_in_advance_min_days as number | null}
        onSave={(v) => patch({ pay_in_advance_min_days: v })}
        type="number"
      />
      <Field
        label={t("policies.lt_field_triggers_custody_handover", "Triggers custody handover")}
        value={ltAny.triggers_custody_handover as boolean | null}
        onSave={(v) => patch({ triggers_custody_handover: v })}
        type="checkbox"
      />
      <Field
        label={t("policies.lt_field_custody_min_duration_days", "Custody min duration days")}
        value={ltAny.custody_min_duration_days as number | null}
        onSave={(v) => patch({ custody_min_duration_days: v })}
        type="number"
      />
      <Field
        label={t("policies.lt_field_gcc_carry_over_days", "GCC carry-over days")}
        value={ltAny.gcc_carry_over_days as number | null}
        onSave={(v) => patch({ gcc_carry_over_days: v })}
        type="number"
      />
      <Field
        label={t("policies.lt_field_gcc_weekend_rule", "GCC weekend rule")}
        value={ltAny.gcc_weekend_rule as string | null}
        onSave={(v) => patch({ gcc_weekend_rule: v })}
      />
      <Field
        label={t("policies.lt_field_half_day_allowed", "Half-day allowed")}
        value={ltAny.half_day_allowed as boolean | null}
        onSave={(v) => patch({ half_day_allowed: v })}
        type="checkbox"
      />
      <Field
        label={t("policies.lt_field_ticket_eligibility_time", "Ticket eligibility time")}
        value={ltAny.ticket_eligibility_time as string | null}
        onSave={(v) => patch({ ticket_eligibility_time: v })}
      />
      <Field
        label={t("policies.lt_field_ticket_cash_or_booking", "Ticket cash/booking")}
        value={ltAny.ticket_cash_or_booking as string | null}
        onSave={(v) => patch({ ticket_cash_or_booking: v })}
        type="select"
        options={["cash", "booking", "either"]}
      />
      <Field
        label={t("policies.lt_field_balanced_auto_zero_at_cycle_end", "Auto-zero at cycle end")}
        value={ltAny.balanced_auto_zero_at_cycle_end as boolean | null}
        onSave={(v) => patch({ balanced_auto_zero_at_cycle_end: v })}
        type="checkbox"
      />
      <Field
        label={t("policies.lt_field_auto_return_based_on_leave_days", "Auto-return based on leave days")}
        value={ltAny.auto_return_based_on_leave_days as boolean | null}
        onSave={(v) => patch({ auto_return_based_on_leave_days: v })}
        type="checkbox"
      />
      <Field
        label={t("policies.lt_field_auto_return_before_days", "Auto-return before days")}
        value={ltAny.auto_return_before_days as number | null}
        onSave={(v) => patch({ auto_return_before_days: v })}
        type="number"
      />
    </div>
  );
}


function ValidationSection({ lt }: { lt: LeaveTypeDetail }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <p className="text-xs italic text-slate-500">
        {t("policies.lt_validation_help")}
      </p>
      <pre className="overflow-x-auto rounded border border-slate-200 bg-slate-50 p-3 text-xs">
        {JSON.stringify(lt.advance_validation, null, 2)}
      </pre>
    </div>
  );
}

// -----------------------------------------------------------------
// New-type dialog
// -----------------------------------------------------------------

function NewLeaveTypeDialog({
  legalEntityId,
  onClose,
  onCreated,
}: {
  legalEntityId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    code: "",
    name_en: "",
    name_ar: "",
    category: "other",
    balance_calculation_type: "fixed_annual",
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.createLeaveType({
        legal_entity_id: legalEntityId,
        code: form.code,
        name_en: form.name_en,
        name_ar: form.name_ar,
        category: form.category,
        balance_calculation_type: form.balance_calculation_type,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
    onError: (e: unknown) => {
      setErrorMsg(e instanceof ApiError ? e.detail : String(e));
    },
  });

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("policies.leave_types_new")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="new-leave-type-form"
            disabled={mutation.isPending}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover disabled:opacity-50"
          >
            {mutation.isPending
              ? t("policies.creating")
              : t("policies.create")}
          </button>
        </div>
      }
    >
        <form
          id="new-leave-type-form"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className="space-y-3 text-sm"
        >
          <label className="block">
            <span className="font-medium text-slate-700">{t("policies.code_required")}</span>
            <input
              required
              value={form.code}
              onChange={(e) =>
                setForm({ ...form, code: e.target.value.toUpperCase() })
              }
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono uppercase"
            />
          </label>
          <BilingualTextWithTranslate
            valueEn={form.name_en}
            valueAr={form.name_ar}
            onChangeEn={(v) => setForm({ ...form, name_en: v })}
            onChangeAr={(v) => setForm({ ...form, name_ar: v })}
            legalEntityId={legalEntityId}
            labelEn={t("policies.name_en_label")}
            labelAr={t("policies.name_ar_label")}
            contextHint="a leave type name"
            required
          />
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="font-medium text-slate-700">{t("policies.allowances_category_label")}</span>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="font-medium text-slate-700">{t("policies.allowances_calc_label")}</span>
              <select
                value={form.balance_calculation_type}
                onChange={(e) =>
                  setForm({
                    ...form,
                    balance_calculation_type: e.target.value,
                  })
                }
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              >
                {BALANCE_CALC_TYPES.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {errorMsg && (
            <p className="rounded bg-rose-50 p-2 text-rose-700">{errorMsg}</p>
          )}
        </form>
    </EntityDrawer>
  );
}

// -----------------------------------------------------------------
// Main page
// -----------------------------------------------------------------

export function LeaveTypesPage() {
  const { t } = useTranslation();
  const { legalEntityId: le, gate } = useLegalEntityGate();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [section, setSection] = useState<Section>("identification");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [showNew, setShowNew] = useState(false);
  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: ["leave-types", le, categoryFilter],
    queryFn: () =>
      api.listLeaveTypes(le, {
        category: categoryFilter || undefined,
      }),
    enabled: !!le,
  });

  const detailQ = useQuery({
    queryKey: ["leave-type", selectedId],
    queryFn: () => api.getLeaveType(selectedId!),
    enabled: !!selectedId,
  });

  const patchMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.updateLeaveType(selectedId!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leave-type", selectedId] });
      qc.invalidateQueries({ queryKey: ["leave-types"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteLeaveType(id),
    onSuccess: () => {
      setSelectedId(null);
      qc.invalidateQueries({ queryKey: ["leave-types"] });
    },
  });

  if (gate) return gate;
  if (listQ.isLoading) return <Loading />;
  if (listQ.error) return <ErrorPanel message={(listQ.error as Error).message} />;

  const items = listQ.data ?? [];
  const selected = detailQ.data ?? null;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("policies.leave_types_title")}
        subtitle={t("policies.leave_types_configured_subtitle", { count: items.length })}
        breadcrumbs={[
          { label: t("nav.policies"), href: "/policies" },
          { label: t("policies.leave_types_title") },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <a
              href="/policies/approval-chains/leave_request"
              className="inline-flex items-center gap-1.5 rounded-sm border border-brand-blue px-3 py-2 text-sm font-medium text-brand-blue hover:bg-brand-blue hover:text-white"
            >
              {t(
                "policies.leave_types_configure_chain",
                "Configure approval chain",
              )}
            </a>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="rounded-sm border border-border bg-surface-default px-2 py-1 text-sm"
            >
              <option value="">{t("policies.leave_types_filter_all")}</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setShowNew(true)}
              className="inline-flex items-center gap-1.5 rounded-sm bg-brand px-3 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover"
            >
              <Plus className="h-4 w-4" /> {t("common.new")}
            </button>
          </div>
        }
      />
      <StatutoryDefaultsCallout domain="leave" />

      <div className="grid grid-cols-12 gap-4">
        {/* Left — list */}
        <div className="col-span-4 rounded-xl border border-slate-200 bg-white">
          <ul className="divide-y divide-slate-100">
            {items.map((lt) => (
              <li key={lt.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(lt.id)}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-start hover:bg-slate-50 ${
                    selectedId === lt.id ? "bg-slate-50" : ""
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{
                        backgroundColor: lt.leave_color ?? "#94a3b8",
                      }}
                    />
                    <BilingualText
                      en={lt.name_en}
                      ar={lt.name_ar}
                      primaryClassName="text-sm font-medium"
                      secondaryClassName="text-xs text-slate-500"
                    />
                  </div>
                  <Badge tone={categoryTone(lt.category)}>{lt.category}</Badge>
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </button>
              </li>
            ))}
            {items.length === 0 && (
              <li className="px-3 py-12 text-center text-sm italic text-text-tertiary">
                {t("policies.leave_types_no_match")}{" "}
                <span className="not-italic">
                  {t(
                    "policies.leave_types_empty_hint",
                    "— clear the search above or create a new leave type.",
                  )}
                </span>
              </li>
            )}
          </ul>
        </div>

        {/* Right — detail with section tabs */}
        <div className="col-span-8 flex flex-col gap-3">
          {selected ? (
            <>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block h-3 w-3 rounded-full"
                        style={{
                          backgroundColor: selected.leave_color ?? "#94a3b8",
                        }}
                      />
                      <h2 className="text-xl font-bold">{selected.name_en}</h2>
                      <Badge tone={categoryTone(selected.category)}>
                        {selected.category}
                      </Badge>
                      {!selected.is_active && (
                        <Badge tone="slate">{t("policies.leave_types_inactive_pill")}</Badge>
                      )}
                    </div>
                    <p className="mt-1 font-mono text-xs text-slate-500">
                      {selected.code}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/policies/leave-types/${selected.id}`}
                      className="text-xs font-medium text-slate-600 underline hover:text-slate-900"
                    >
                      {t("policies.leave_types_open_detail")}
                    </Link>
                    <button
                      type="button"
                      onClick={() =>
                        patchMutation.mutate({ is_active: !selected.is_active })
                      }
                      className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <Check className="h-3.5 w-3.5" />
                      {selected.is_active
                        ? t("policies.allowances_deactivate")
                        : t("policies.allowances_activate")}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteMutation.mutate(selected.id)}
                      className="rounded p-1 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                      aria-label={t("common.delete")}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1">
                {SECTIONS.map(({ key, labelKey, icon: Icon }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSection(key)}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                      section === key
                        ? "bg-brand-blue text-white"
                        : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {t(labelKey)}
                  </button>
                ))}
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4">
                {section === "identification" && (
                  <IdentificationSection
                    lt={selected}
                    patch={(b) => patchMutation.mutate(b)}
                  />
                )}
                {section === "eligibility" && (
                  <EligibilitySection
                    lt={selected}
                    patch={(b) => patchMutation.mutate(b)}
                  />
                )}
                {section === "balance" && (
                  <BalanceSection
                    lt={selected}
                    patch={(b) => patchMutation.mutate(b)}
                  />
                )}
                {section === "payment" && (
                  <PaymentSection
                    lt={selected}
                    patch={(b) => patchMutation.mutate(b)}
                  />
                )}
                {section === "calculation" && (
                  <CalculationSection
                    lt={selected}
                    patch={(b) => patchMutation.mutate(b)}
                  />
                )}
                {section === "validation" && (
                  <ValidationSection lt={selected} />
                )}
                {section === "pr1_knobs" && (
                  <Pr1KnobsSection
                    lt={selected}
                    patch={(b) => patchMutation.mutate(b)}
                  />
                )}
              </div>
            </>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm italic text-slate-500">
              {t("app.lt_pick_left")}
            </div>
          )}
        </div>
      </div>

      {showNew && (
        <NewLeaveTypeDialog
          legalEntityId={le}
          onClose={() => setShowNew(false)}
          onCreated={() =>
            qc.invalidateQueries({ queryKey: ["leave-types"] })
          }
        />
      )}
    </div>
  );
}
